import { graphqlClient } from '@/lib/graphql-client';
import { toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import {
  buildGetEmployeesLookupQuery,
  buildGetSurveyBatchesQuery,
  buildGetSurveyFormsLookupQuery,
  buildGetSurveySubmissionsQuery,
} from '../graphql/queries';
import {
  DELETE_SURVEY_BATCH_MUTATION,
  DELETE_SURVEY_SUBMISSION_MUTATION,
  INSERT_SURVEY_BATCH_MUTATION,
  INSERT_SURVEY_SUBMISSION_MUTATION,
} from '../graphql/mutations';
import {
  EmployeesLookupResult,
  GetSurveyBatchesParams,
  GetSurveySubmissionsParams,
  SendSurveyInput,
  SurveyBatchesPagingResult,
  SurveyFormOption,
  SurveySubmissionsPagingResult,
} from '../types/survey.types';

export const getSurveyBatches = async (
  params: GetSurveyBatchesParams
): Promise<{ SurveyBatches: SurveyBatchesPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetSurveyBatchesQuery(filter, pageNo, pageSize, order),
  });
};

export const getSurveyBatchById = async (
  itemId: string
): Promise<{ SurveyBatches: SurveyBatchesPagingResult }> =>
  graphqlClient.query({
    query: buildGetSurveyBatchesQuery({ ItemId: itemId }, 1, 1),
  });

export const getSurveySubmissions = async (
  params: GetSurveySubmissionsParams
): Promise<{ SurveySubmissions: SurveySubmissionsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetSurveySubmissionsQuery(filter, pageNo, pageSize, order),
  });
};

export const getSurveySubmissionById = async (
  itemId: string
): Promise<{ SurveySubmissions: SurveySubmissionsPagingResult }> =>
  graphqlClient.query({
    query: buildGetSurveySubmissionsQuery({ ItemId: itemId }, 1, 1),
  });

export const getSurveyFormsLookup = async (): Promise<{
  SurveyForms: { items: SurveyFormOption[] };
}> =>
  graphqlClient.query({
    query: buildGetSurveyFormsLookupQuery({}, 1, 300),
  });

export const getSurveyFormLookupById = async (
  formId: string
): Promise<{ SurveyForms: { items: SurveyFormOption[] } }> =>
  graphqlClient.query({
    query: buildGetSurveyFormsLookupQuery({ ItemId: formId }, 1, 1),
  });

export const getEmployeesLookup = async (
  keyword = ''
): Promise<{ Users: EmployeesLookupResult }> => {
  const filter = keyword ? { DisplayName: { $regex: keyword, $options: 'i' } } : {};
  return graphqlClient.query({
    query: buildGetEmployeesLookupQuery(filter, 1, 50),
  });
};

export const sendSurvey = async (input: SendSurveyInput) => {
  // Deadline is a date-only value (the picked day at 00:00:00 UTC); sent/requested are UTC instants
  // stamped with the organization's zone.
  const deadline = toDateOnlyValue(input.Deadline);
  const now = new Date().toISOString();
  const timeZone = getOrganizationTimeZone();
  const batchResult = (await graphqlClient.mutate({
    query: INSERT_SURVEY_BATCH_MUTATION,
    variables: {
      input: {
        Title: input.Title,
        Description: input.Description,
        SurveyFormId: input.SurveyFormId,
        SentDateUtc: now,
        Deadline: deadline,
        Timezone: timeZone,
        TotalRecipients: input.Recipients.length,
        CompletedCount: 0,
      },
    },
  })) as { insertSurveyBatch: { itemId: string } };
  const surveyBatchId = batchResult.insertSurveyBatch.itemId;

  await Promise.all(
    input.Recipients.map((recipient) =>
      graphqlClient.mutate({
        query: INSERT_SURVEY_SUBMISSION_MUTATION,
        variables: {
          input: {
            SurveyBatchId: surveyBatchId,
            SurveyFormId: input.SurveyFormId,
            SurveyTitle: input.Title,
            SurveyDescription: input.Description,
            Deadline: deadline,
            RequestedDateUtc: now,
            Timezone: timeZone,
            UserId: recipient.UserId,
            UserName: recipient.UserName,
            UserEmail: recipient.UserEmail,
            Status: 'Pending',
          },
        },
      })
    )
  );

  return surveyBatchId;
};

export const deleteSurveyBatch = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  return graphqlClient.mutate({
    query: DELETE_SURVEY_BATCH_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  });
};

export const deleteSurveySubmission = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  return graphqlClient.mutate({
    query: DELETE_SURVEY_SUBMISSION_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  });
};
